import {RoleBasedPermission} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {type AxiosError} from 'axios'
import {useCallback, useEffect, useMemo} from 'react'

import {ImportReportModal} from '../components'
import {
  buildImportPlantOptions,
  isValidPlantForImport,
  resolveSiteNameForPlant,
  type ImportPlantOption,
  parseExcelFile
} from '../helpers'
import {
  useImportDuplicateCheck,
  useImportReport,
  useImportReportState,
  useRiskSurveyPlantsByPermission,
  useRiskSurveyTranslation
} from '../hooks'

import {normalizeDateTimeForApi, normalizeOptionalDateTimeForApi} from './importReport.helpers'

type ImportReportContainerProps = {
  open: boolean
  onClose: () => void
  upmId?: string
  siteName?: string
}

export const ImportReportContainer = ({
  open,
  onClose,
  upmId: externalUpmId,
  siteName: externalSiteName
}: ImportReportContainerProps) => {
  const {t} = useRiskSurveyTranslation()
  const [state, dispatch] = useImportReportState(externalUpmId, externalSiteName)
  const {
    step,
    isLoading,
    parsedRows,
    parseErrors,
    excludedRows,
    importResultStatus,
    importResultMessage,
    upmId,
    siteName,
    duplicateImportCheck
  } = state
  const needsPlantInput = !externalUpmId
  const upmPlants = useUpmPlantsContext()
  const {plantIds} = useRiskSurveyPlantsByPermission(RoleBasedPermission.HproduceRoleQualityManager)

  const plantOptions: ImportPlantOption[] = useMemo(() => {
    if (!needsPlantInput) {
      return []
    }
    return buildImportPlantOptions(plantIds, upmPlants, (id) =>
      t('plant.fallbackName', {upmId: id})
    )
  }, [needsPlantInput, plantIds, upmPlants, t])

  const selectedPlantValid = useMemo(() => {
    if (!needsPlantInput) {
      return true
    }
    return isValidPlantForImport(upmId, plantOptions)
  }, [needsPlantInput, upmId, plantOptions])

  const {mutate: importReport, isPending: isSubmitting} = useImportReport()
  const {mutate: checkImportDuplicate, isPending: isCheckingImportDuplicate} =
    useImportDuplicateCheck()

  const duplicateRiNumbers = useMemo(() => {
    const seen = new Map<string, number>()
    parsedRows.forEach((row, idx) => {
      if (!excludedRows.has(idx)) {
        seen.set(row.riNumber, (seen.get(row.riNumber) ?? 0) + 1)
      }
    })
    return Array.from(seen.entries())
      .filter(([, count]) => count > 1)
      .map(([ri]) => ri)
  }, [parsedRows, excludedRows])

  const handleToggleRow = useCallback(
    (index: number) => dispatch({type: 'TOGGLE_ROW', index}),
    [dispatch]
  )

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (needsPlantInput && !selectedPlantValid) return
      dispatch({type: 'START_FILE_PARSE'})
      try {
        const result = await parseExcelFile(file)
        dispatch({type: 'FILE_PARSED', parsedRows: result.validRows, parseErrors: result.errors})
      } catch {
        dispatch({type: 'FILE_PARSE_FAILED'})
      }
    },
    [needsPlantInput, selectedPlantValid, dispatch]
  )

  const handleBack = useCallback(() => {
    dispatch({type: 'BACK_TO_UPLOAD'})
  }, [dispatch])

  const handleBackToPreviewAfterError = useCallback(() => {
    dispatch({type: 'BACK_TO_PREVIEW'})
  }, [dispatch])

  const handleImport = useCallback(() => {
    const includedRows = parsedRows
      .filter((_, idx) => !excludedRows.has(idx))
      .map((row) => ({
        ...row,
        dateIssued: normalizeDateTimeForApi(row.dateIssued),
        indicativeTimeScale: normalizeOptionalDateTimeForApi(row.indicativeTimeScale)
      }))

    dispatch({type: 'START_SUBMIT'})

    if (needsPlantInput && !selectedPlantValid) {
      dispatch({type: 'IMPORT_ERROR', message: t('import.invalidPlantSelection')})
      return
    }

    importReport(
      {
        upmId,
        siteName,
        riskImprovementActions: includedRows
      },
      {
        onSuccess: () => {
          dispatch({type: 'IMPORT_SUCCESS', message: t('import.success')})
        },
        onError: (error) => {
          const apiError = error as AxiosError<{detail?: string; title?: string}>
          const detail =
            apiError.response?.data?.detail ??
            apiError.response?.data?.title ??
            apiError.message ??
            t('import.error')
          dispatch({type: 'IMPORT_ERROR', message: detail})
        }
      }
    )
  }, [
    parsedRows,
    excludedRows,
    upmId,
    siteName,
    importReport,
    needsPlantInput,
    selectedPlantValid,
    t,
    dispatch
  ])

  useEffect(() => {
    if (!needsPlantInput) {
      return
    }
    dispatch({type: 'SET_SITE_NAME', siteName: resolveSiteNameForPlant(upmId, plantOptions)})
  }, [needsPlantInput, upmId, plantOptions, dispatch])

  useEffect(() => {
    if (step !== 'preview') {
      dispatch({type: 'SET_DUPLICATE_CHECK', check: null})
      return
    }

    if (!upmId.trim() || !siteName.trim() || !selectedPlantValid) {
      dispatch({type: 'SET_DUPLICATE_CHECK', check: null})
      return
    }

    const includedRiNumbers = parsedRows
      .filter((_, idx) => !excludedRows.has(idx))
      .map((row) => row.riNumber)

    if (includedRiNumbers.length === 0) {
      dispatch({type: 'SET_DUPLICATE_CHECK', check: null})
      return
    }

    const timeoutId = window.setTimeout(() => {
      checkImportDuplicate(
        {
          upmId,
          siteName,
          riNumbers: includedRiNumbers
        },
        {
          onSuccess: (result) => {
            dispatch({type: 'SET_DUPLICATE_CHECK', check: result})
          },
          onError: () => {
            dispatch({type: 'SET_DUPLICATE_CHECK', check: null})
          }
        }
      )
    }, 300)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    step,
    upmId,
    siteName,
    selectedPlantValid,
    parsedRows,
    excludedRows,
    checkImportDuplicate,
    dispatch
  ])

  const handleClose = useCallback(() => {
    dispatch({
      type: 'RESET',
      defaultUpmId: externalUpmId ?? '',
      defaultSiteName: externalSiteName ?? ''
    })
    onClose()
  }, [dispatch, externalUpmId, externalSiteName, onClose])

  return (
    <ImportReportModal
      open={open}
      onClose={handleClose}
      step={step}
      onFileSelected={handleFileSelected}
      isLoading={isLoading}
      parsedRows={parsedRows}
      parseErrors={parseErrors}
      excludedRows={excludedRows}
      onToggleRow={handleToggleRow}
      onBack={handleBack}
      onImport={handleImport}
      isSubmitting={isSubmitting}
      duplicateRiNumbers={duplicateRiNumbers}
      importResultStatus={importResultStatus}
      importResultMessage={importResultMessage}
      onImportResultBackToPreview={handleBackToPreviewAfterError}
      needsPlantInput={needsPlantInput}
      plantId={upmId}
      siteName={siteName}
      plantOptions={plantOptions}
      onPlantIdChange={(value: string) => dispatch({type: 'SET_UPM_ID', upmId: value})}
      duplicateImportCheck={duplicateImportCheck}
      isCheckingImportDuplicate={isCheckingImportDuplicate}
    />
  )
}
